// src/pages/Home.jsx
import { useEffect, useState, useRef, lazy, Suspense, useCallback, useMemo } from "react";
import { useInView } from "../hooks/useInView";
import { useSite } from "../contexts/SiteConfig";
import { api } from "../api/client";
import ProductCard from "../components/ProductCard";
import { Link, useNavigate } from "react-router-dom";
import { assetUrl } from "../api/asset";
import '../styles/style-button.css';
import ScrollToTopButton from "../components/ScrollToTopButton";
import SEO from "../components/SEO";
import {
  ArrowRight, ChevronLeft, ChevronRight,
  RefreshCw, Trophy, Lock, AlertTriangle, CheckCircle, Gift, Copy, Quote,
  Sparkles, Puzzle as PuzzleIcon, Crown
} from "lucide-react";

// --- THEME ASSETS ---
const parchmentBg = "url('/images-webp/homepage/parchment-bg.webp')";
const mandalaBg = "url('/images-webp/homepage/mandala-bg.webp')";


const DEFAULT_HOME_TITLE = "Kiddos Intellect - Premium Children's Books | Educational Learning Materials";
const DEFAULT_HOME_DESC = "Discover hand-picked children's books and educational materials. Healthy Minds Grow Beyond Screens. Shop premium learning resources for curious young minds in India.";

export default function Home() {
  const { homepage, seo } = useSite();
  const isReady =
    Array.isArray(homepage?.blocks) && homepage.blocks.length > 0;
  const activeVariant = seo?.homepageVariants?.find(v => v.active);
  return (
    <div className="bg-[#FAF7F2] min-h-screen text-[#5C4A2E] selection:bg-[#F3E5AB] selection:text-[#3E2723] pb-6 md:pb-20 overflow-x-hidden" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>

      <SEO
        title={activeVariant?.title || DEFAULT_HOME_TITLE}
        description={activeVariant?.description || DEFAULT_HOME_DESC}
        image="/images/homepage/hero-banner.jpg"
        type="website"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Kiddos Intellect",
          "description": "Premium children's books and educational materials provider",
          "url": typeof window !== 'undefined' ? window.location.origin : '',
          "logo": typeof window !== 'undefined' ? `${window.location.origin}/logo.png` : ''
        }}
      />

      {/* Global Background Texture */}
      <div
        className="fixed inset-0 pointer-events-none opacity-100 z-0"
        style={{ backgroundImage: parchmentBg, backgroundSize: 'cover' }}
      />

      {/* ✅ FIRST PAINT — static hero shows instantly before API loads */}
      {!isReady && (
        <div className="max-w-7xl 2xl:max-w-[1800px] mx-auto relative z-10 px-4 sm:px-6 lg:px-8">
          {/* Hero image — fixed height matches dynamic hero so no layout shift */}
          <div className="pt-8 md:pt-12 pb-8 md:pb-12">
            <picture>
              <source media="(max-width: 767px)" srcSet="/images/homepageslider-mobile.webp" />
              <img
                src="/images/homepageslider.jpg"
                alt="Kiddos Intellect"
                loading="eager"
                fetchpriority="high"
                decoding="sync"
                width="1200"
                height="500"
                className="w-full rounded-[2.5rem] object-cover h-[400px] md:h-[500px] shadow-[0_20px_60px_rgba(62,39,35,0.3)]"
              />
            </picture>
          </div>

          {/* Skeleton sections below — preserve total page height to prevent CLS */}
          {[1, 2, 3].map((i) => (
            <div key={i} className="py-12">
              <div className="h-8 w-64 bg-[#3E2723]/10 rounded mb-6 animate-pulse" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[1, 2, 3, 4].map((j) => (
                  <div key={j} className="h-64 rounded-2xl bg-[#3E2723]/10 animate-pulse" />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ✅ REAL CONTENT */}
      {isReady && (
        <div className="max-w-7xl 2xl:max-w-[1800px] mx-auto relative z-10">
          {homepage.blocks.map((b, i) => (
            <Block key={i} block={b} />
          ))}
        </div>
      )}

      <ScrollToTopButton />
    </div>
  );
}



/* ----------------------------- Blocks Renderer ---------------------------- */
function Block({ block }) {
  // Must be at top level — used only when block.type === "puzzle"
  const handlePuzzleWin = useCallback(async () => {
    try {
      await api.post("/customer/puzzle/claim");
    } catch (_) {
      // Silently fail — localStorage fallback still works for UI display
    }
  }, []);

  const spacing = block.spacing || {
    paddingTop: "normal",
    paddingBottom: "normal",
    paddingX: "normal",
    backgroundColor: ""
  };

  const getSpacingClasses = () => {
    const classes = [];
    if (spacing.paddingTop === "none") classes.push("pt-0");
    else if (spacing.paddingTop === "small") classes.push("pt-2 md:pt-6");
    else if (spacing.paddingTop === "normal") classes.push("pt-4 md:pt-12");
    else if (spacing.paddingTop === "large") classes.push("pt-6 md:pt-20");

    if (spacing.paddingBottom === "none") classes.push("pb-0");
    else if (spacing.paddingBottom === "small") classes.push("pb-2 md:pb-6");
    else if (spacing.paddingBottom === "normal") classes.push("pb-4 md:pb-12");
    else if (spacing.paddingBottom === "large") classes.push("pb-6 md:pb-20");

    if (spacing.paddingX === "none") classes.push("px-0");
    else if (spacing.paddingX === "small") classes.push("px-2 sm:px-4");
    else if (spacing.paddingX === "normal") classes.push("px-4 sm:px-6 lg:px-8");
    else if (spacing.paddingX === "large") classes.push("px-8 sm:px-12 lg:px-16");

    return classes.join(" ");
  };

  const containerClasses = getSpacingClasses();
  const containerStyle = spacing.backgroundColor ? { backgroundColor: spacing.backgroundColor } : {};

  // --- STATIC HERO BLOCK ---
  if (block.type === "hero") {
    return (
      <div className={containerClasses} style={containerStyle}>
        <section className="relative rounded-[2.5rem] overflow-hidden bg-[#3E2723] text-[#F3E5AB] shadow-[0_20px_50px_rgba(62,39,35,0.3)] group min-h-[400px] md:min-h-[500px] grid grid-cols-1 md:grid-cols-2 items-center border border-[#D4AF37]/30">
          {/* Texture Overlay */}
          <div className="absolute inset-0 z-0 opacity-10 mix-blend-overlay pointer-events-none" style={{ backgroundImage: mandalaBg, backgroundSize: '400px' }} />

          <div className="p-8 md:p-12 lg:p-16 z-10 relative">
            <div className="absolute top-0 left-0 w-32 h-32 bg-[#D4AF37] rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-40"></div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-['Playfair_Display'] font-bold mb-4 md:mb-6 leading-tight drop-shadow-md text-white">{block.title}</h2>
            <p className="text-[#D4AF37] text-lg md:text-xl mb-8 leading-relaxed max-w-lg font-light font-['Inter']">{block.subtitle}</p>
            {block.ctaText && (
              <Link to={block.ctaHref || "/catalog"} className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#C59D5F] to-[#B0894C] text-white rounded-full font-bold text-lg hover:from-[#D4AF37] hover:to-[#C59D5F] transition-all shadow-lg hover:shadow-[#D4AF37]/40 active:scale-95 group-hover:translate-x-1 duration-300 font-['Cinzel'] tracking-wide border border-[#F3E5AB]/30">
                {block.ctaText} <ArrowRight className="w-5 h-5" />
              </Link>
            )}
          </div>
          {block.image && (
            <div className="relative w-full h-64 md:h-full overflow-hidden">
              <img src={assetUrl(block.image, "hero")} alt="" loading="eager" fetchpriority="high" decoding="async" className="absolute inset-0 w-full h-full object-contain transition-transform duration-700 ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#3E2723] to-transparent md:bg-gradient-to-l opacity-90"></div>
            </div>
          )}
        </section>
      </div>
    );
  }

  // --- HERO SLIDER ---
  if (block.type === "hero-slider") {
    return (
      <div className={containerClasses} style={containerStyle}>
        <HeroSlider slides={block.slides} />
      </div>
    );
  }

  // --- STANDARD SLIDER ---
  if (block.type === "slider") {
    // Break out of max-w container for full-viewport-width on desktop
    return (
      <div
        className="relative pb-2 md:pb-3"
        style={{
          width: '100vw',
          marginLeft: 'calc(50% - 50vw)',
          ...containerStyle,
        }}
      >
        <HomepageSlider slides={block.slides} height={block.sliderHeight} fullWidth />
      </div>
    );
  }

  // --- STAGGERED WALL (example.html style) ---
  if (block.type === "staggered-wall") {
    return (
      <div className={containerClasses} style={containerStyle}>
        <StaggeredWall block={block} />
      </div>
    );
  }

  // --- PUZZLE GAME (Royal Board) ---
  if (block.type === "puzzle") {
    const levels = block.levels || [
      { difficulty: "easy", image: block.image, gridSize: 3, label: "Level 1" },
      { difficulty: "medium", image: block.image, gridSize: 4, label: "Level 2" },
      { difficulty: "hard", image: block.image, gridSize: 5, maxMoves: 100, label: "Level 3" }
    ];

    return (
      <div className={`${containerClasses} !pb-0`} style={containerStyle}>
        <div className="text-center mb-4 sm:mb-10">
          <div className="inline-block p-3 rounded-full bg-[#FFF9E6] border border-[#D4AF37]/30 mb-2 sm:mb-4 shadow-sm">
            <PuzzleIcon className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h2 className="text-3xl md:text-5xl font-['Cinzel'] font-bold text-[#3E2723] mb-3">
            {block.title}
          </h2>
          {block.subtitle && <p className="text-[#8A7A5E] text-lg max-w-2xl mx-auto">{block.subtitle}</p>}
        </div>
        <Suspense fallback={
          <div className="max-w-5xl mx-auto h-[500px] rounded-[1.5rem] bg-[#3E2723]/10 animate-pulse" />
        }>
          <ProgressivePuzzleGame
            levels={levels}
            grandWinMessage={block.winMessage}
            rewardImage={block.rewardImage}
            onWin={handlePuzzleWin}
          />
        </Suspense>
      </div>
    );
  }

  // --- BANNER (Glassy) ---
  if (block.type === "banner") {
    const imageUrl = assetUrl(block.image, "hero");
    return (
      <div className={containerClasses} style={containerStyle}>
        <a href={block.ctaLink || "/"} className="block rounded-[2rem] overflow-hidden shadow-[0_15px_40px_rgba(62,39,35,0.15)] group relative isolate h-full w-full border border-[#D4AF37]/20">
          {/* Blurred BG */}
          <div
            className="absolute inset-0 -z-10 bg-cover bg-center blur-2xl scale-125 opacity-60 transition-all duration-500 group-hover:opacity-80"
            style={{ backgroundImage: `url(${imageUrl})` }}
            aria-hidden="true"
          ></div>

          {/* Main Image */}
          <img
            src={imageUrl}
            alt={block.alt || ""}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-contain relative z-10 transition-all duration-700 group-hover:scale-[1.02]"
          />

          {/* Shine */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 z-20 pointer-events-none" />
        </a>
      </div>
    );
  }

  // --- GRID SECTION ---
  if (block.type === "grid") {
    return (
      <div className={containerClasses} style={containerStyle}>
        <GridSection title={block.title} query={block.query} />
      </div>
    );
  }

  if (block.type === "category-browser") {
    return (
      <div className={containerClasses} style={containerStyle}>
        <CategoryBrowser block={block} />
      </div>
    );
  }

  if (block.type === "html") {
    return (
      <div style={containerStyle} dangerouslySetInnerHTML={{ __html: block.html }} />
    );
  }

  // --- VIDEO SHOWCASE ---
  if (block.type === "video-showcase") {
    const videos = Array.isArray(block.videos) ? block.videos.filter(v => v.videoUrl) : [];
    if (!videos.length) return null;
    return (
      <div className={containerClasses} style={containerStyle}>
        <VideoShowcase block={block} videos={videos} />
      </div>
    );
  }

  return null;
}

/* --------------------------------------------------------------------------
   PUZZLE GAME COMPONENT â€" lazy-loaded so its JS is not in the initial bundle
--------------------------------------------------------------------------- */
const ProgressivePuzzleGame = lazy(() => import("../components/ProgressivePuzzleGame"));


/* ------------------------- HERO SLIDER COMPONENT ------------------------- */
// ... (HeroSlider remains unchanged) ...
function HeroSlider({ slides }) {
  const [current, setCurrent] = useState(0);
  const timeoutRef = useRef(null);
  const items = Array.isArray(slides) ? slides : [];
  const length = items.length;

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (length > 1) {
      timeoutRef.current = setTimeout(() => setCurrent((prev) => (prev === length - 1 ? 0 : prev + 1)), 3000);
    }
    return () => clearTimeout(timeoutRef.current);
  }, [current, length]);

  const nextSlide = () => setCurrent(current === length - 1 ? 0 : current + 1);
  const prevSlide = () => setCurrent(current === 0 ? length - 1 : current - 1);

  if (!length) return null;

  const getHeight = (h) => {
    switch (h) {
      case 'small': return 'min-h-[350px] md:min-h-[400px]';
      case 'large': return 'min-h-[500px] md:min-h-[600px]';
      case 'medium':
      default: return 'min-h-[400px] md:min-h-[500px]';
    }
  };

  // Lock to first slide height — prevents layout shift (CLS) on slide advance
  const containerHeight = getHeight(items[0].height);

  return (
    <div className={`relative w-full rounded-[2.5rem] overflow-hidden shadow-[0_20px_60px_rgba(62,39,35,0.3)] flex items-center group transition-all duration-300 ${containerHeight} border border-[#D4AF37]/30`}>
      {items.map((slide, idx) => (
        <div key={idx} className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${current === idx ? "opacity-100 z-10" : "opacity-0 z-0"}`} style={{ backgroundColor: slide.bgColor || '#3E2723' }}>
          {slide.layout === 'full' && slide.image && (
            <>
              <img src={assetUrl(slide.image, "hero")} alt={slide.title} loading={idx === 0 ? "eager" : "lazy"} fetchpriority={idx === 0 ? "high" : "auto"} decoding="async" className="absolute inset-0 w-full h-full object-cover object-center" />
              <div className="absolute inset-0 bg-black/40"></div>
            </>
          )}
          {slide.layout !== 'full' && (
            <>
              <div className="absolute inset-0 z-0 opacity-10 pointer-events-none mix-blend-overlay" style={{ backgroundImage: mandalaBg, backgroundSize: '400px' }} />
              {slide.image && (
                <div className="absolute inset-0 md:left-1/3 w-full md:w-2/3 h-full overflow-hidden">
                  <img src={assetUrl(slide.image, "hero")} alt={slide.title} loading={idx === 0 ? "eager" : "lazy"} fetchpriority={idx === 0 ? "high" : "auto"} decoding="async" className={`w-full h-full ${slide.objectFit === 'contain' ? 'object-contain p-8 md:p-12' : 'object-contain'} object-center opacity-40 md:opacity-100 transition-transform duration-700 ease-out group-hover:scale-105`} />
                  {slide.objectFit === 'cover' && <div className="absolute inset-0" style={{ background: `linear-gradient(to right, ${slide.bgColor || '#3E2723'} 5%, transparent 100%), linear-gradient(to top, ${slide.bgColor || '#3E2723'} 0%, transparent 50%)` }}></div>}
                </div>
              )}
            </>
          )}
          <div className="relative z-20 h-full px-8 md:px-16 flex items-center">
            <div className={`w-full md:w-1/2 pt-12 md:pt-0 ${slide.layout === 'full' ? 'md:mx-auto md:w-2/3 md:text-center text-center' : 'text-left'}`}>
              <h2 className="text-4xl md:text-6xl font-['Playfair_Display'] font-bold mb-6 tracking-tight leading-tight drop-shadow-md" style={{ color: slide.textColor || '#F3E5AB' }}>{slide.title}</h2>
              <p className="text-lg md:text-xl font-light mb-8 opacity-90 leading-relaxed font-['Inter']" style={{ color: slide.textColor || '#F3E5AB', maxWidth: '500px' }}>{slide.subtitle}</p>
              {slide.ctaText && (
                <Link to={slide.ctaLink || "/catalog"} className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#C59D5F] to-[#B0894C] text-white rounded-full font-bold text-lg hover:from-[#D4AF37] hover:to-[#C59D5F] transition-all shadow-lg active:scale-95 group-hover:translate-x-1 duration-300 font-['Cinzel'] border border-[#F3E5AB]/30">{slide.ctaText} <ArrowRight className="w-5 h-5" /></Link>
              )}
            </div>
          </div>
        </div>
      ))}
      {length > 1 && (
        <>
          <button onMouseDown={e => e.preventDefault()} onClick={prevSlide} className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-black/20 hover:bg-[#D4AF37] text-white rounded-full flex items-center justify-center backdrop-blur-md transition-all z-30 border border-white/10 hover:border-[#D4AF37]"><ChevronLeft className="w-6 h-6" /></button>
          <button onMouseDown={e => e.preventDefault()} onClick={nextSlide} className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-black/20 hover:bg-[#D4AF37] text-white rounded-full flex items-center justify-center backdrop-blur-md transition-all z-30 border border-white/10 hover:border-[#D4AF37]"><ChevronRight className="w-6 h-6" /></button>
          <div className="absolute bottom-8 left-10 md:left-20 flex gap-3 z-30">
            {items.map((_, idx) => <button key={idx} onMouseDown={e => e.preventDefault()} onClick={() => setCurrent(idx)} className={`h-1.5 rounded-full transition-all duration-500 ${current === idx ? "w-8 bg-[#D4AF37]" : "w-2 bg-white/40 hover:bg-white/60"}`} />)}
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------- Standard Image Slider — Cinematic Fade ------------------------- */
function HomepageSlider({ slides }) {
  const [current, setCurrent] = useState(0);
  const timeoutRef = useRef(null);

  const items = (Array.isArray(slides) ? slides : []).map(s => ({
    desktop: assetUrl(s.desktopImage || s.image, "hero"),
    mobile: assetUrl(s.mobileImage || s.desktopImage || s.image, "thumb"),
    link: s.link || "#",
    alt: s.alt || "Slider Image",
  }));
  const length = items.length;

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setCurrent(c => (c + 1) % length), 4800);
    return () => clearTimeout(timeoutRef.current);
  }, [current, length]);

  const go = (dir) => setCurrent(c => ((c + dir) + length) % length);

  if (!length) return null;

  return (
    <div className="relative w-full group/slider">

      {/* ── Slide images ── */}
      <div className="relative overflow-hidden shadow-[0_8px_40px_rgba(62,39,35,0.2)]">

        {/* Hidden sizer — locks height, prevents CLS */}
        <picture className="block w-full opacity-0 pointer-events-none select-none" aria-hidden="true">
          <source media="(max-width: 767px)" srcSet={items[0].mobile} />
          <source media="(min-width: 768px)" srcSet={items[0].desktop} />
          <img
            src={items[0].desktop}
            alt=""
            loading="eager"
            decoding="async"
            className="block w-full"
            draggable={false}
          />
        </picture>

        {items.map((slide, idx) => (
          <div
            key={idx}
            className="transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)]"
            style={idx !== current ? {
              position: 'absolute', inset: 0,
              opacity: 0, transform: 'scale(0.98)',
              pointerEvents: 'none',
            } : {
              position: 'absolute', inset: 0,
              opacity: 1, transform: 'scale(1)',
            }}
          >
            <Link to={slide.link} className="block w-full h-full">
              <picture className="block w-full h-full">
                <source media="(max-width: 767px)" srcSet={slide.mobile} />
                <source media="(min-width: 768px)" srcSet={slide.desktop} />
                <img
                  src={slide.desktop}
                  alt={slide.alt}
                  loading={idx === 0 ? "eager" : "lazy"}
                  fetchpriority={idx === 0 ? "high" : "auto"}
                  decoding="async"
                  className="w-full h-full object-contain md:object-cover block select-none"
                  draggable={false}
                />
              </picture>
            </Link>
          </div>
        ))}

        {/* ── Arrows — inside, hidden until hover ── */}
        {length > 1 && (
          <>
            <button
              onMouseDown={e => e.preventDefault()}
              onClick={() => go(-1)}
              className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-20
                w-11 h-11 rounded-full
                bg-black/30 hover:bg-[#D4AF37]
                border border-white/20 hover:border-[#D4AF37]
                backdrop-blur-md text-white
                flex items-center justify-center
                transition-all duration-300
                opacity-0 group-hover/slider:opacity-100
                -translate-x-2 group-hover/slider:translate-x-0
                active:scale-90"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onMouseDown={e => e.preventDefault()}
              onClick={() => go(1)}
              className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-20
                w-11 h-11 rounded-full
                bg-black/30 hover:bg-[#D4AF37]
                border border-white/20 hover:border-[#D4AF37]
                backdrop-blur-md text-white
                flex items-center justify-center
                transition-all duration-300
                opacity-0 group-hover/slider:opacity-100
                translate-x-2 group-hover/slider:translate-x-0
                active:scale-90"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Dots — inside the image as overlay, zero extra space */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/20 backdrop-blur-sm">
              {items.map((_, idx) => (
                <button
                  key={idx}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => setCurrent(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                  className={`rounded-full transition-all duration-500 ease-out ${current === idx
                      ? 'w-6 h-1.5 bg-[#D4AF37] shadow-[0_0_6px_rgba(212,175,55,0.8)]'
                      : 'w-1.5 h-1.5 bg-white/50 hover:bg-white/80'
                    }`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------- Staggered Wall — Infinite Focus Pan ------------------------- */
function StaggeredWall({ block = {} }) {
  const {
    eyebrow = "Uniform Tiling",
    heading = "Staggered\nColumns",
    description = "",
    autoplay = true,
    intervalMs = 2200,
    height = "800",
    showText = true,
    textPosition = "left",
    items = [],
  } = block;

  const viewportRef = useRef(null);
  const autoplayRef = useRef(null);
  const bagRef = useRef([]);
  const vpSizeRef = useRef({ w: 0, h: 0 });
  const activeIdRef = useRef(null);
  const [viewport, setViewport] = useState({ w: 0, h: 0 });
  const [transitionEnabled, setTransitionEnabled] = useState(false);

  const cleanItems = useMemo(() => {
    return (Array.isArray(items) ? items : [])
      .filter(it => it && it.image)
      .map((it, idx) => ({
        id: idx,
        src: assetUrl(it.image, "hero"),
        alt: it.alt || "Wall image",
        link: it.link || "",
        objectFit: it.objectFit || "cover",
      }));
  }, [items]);

  const CARD_W = 320;
  const CARD_H = 420;
  const GAP_X = 24;
  const GAP_Y = 24;
  const COLS = 3;
  const LEFT_MARGIN = 30;
  // Classic stagger: col0 neutral, col1 drops ~half card, col2 drops ~quarter
  const COL_OFFSETS = [0, 210, 80];

  const base = useMemo(() => {
    return cleanItems.map((it, index) => {
      const col = index % COLS;
      const row = Math.floor(index / COLS);
      const top = COL_OFFSETS[col] + row * (CARD_H + GAP_Y);
      const left = LEFT_MARGIN + col * (CARD_W + GAP_X);
      return { ...it, top, left };
    });
  }, [cleanItems]);

  const baseById = useMemo(() => {
    const map = new Map();
    base.forEach(b => map.set(b.id, b));
    return map;
  }, [base]);

  const clusterWidth =
    LEFT_MARGIN * 2 + (COLS - 1) * (CARD_W + GAP_X) + CARD_W;
  const maxBottom = base.reduce((m, b) => Math.max(m, b.top + CARD_H), 0);
  const clusterHeight = Math.max(900, maxBottom + 180);

  const defaultStart = Math.min(4, Math.max(0, Math.floor(base.length / 2)));
  const [activeId, setActiveId] = useState(defaultStart);
  const [transform, setTransform] = useState("translate3d(0px, 0px, 0)");

  // Keep refs always in sync so interval callbacks never read stale closure values
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

  const computeTransformFor = (id, vp) => {
    const target = baseById.get(id);
    if (!target || !vp?.w || !vp?.h) return "translate3d(0px, 0px, 0)";
    const cardCenterX = target.left + CARD_W / 2;
    const cardCenterY = target.top + CARD_H / 2;
    const vpCenterX = vp.w / 2;
    const vpCenterY = vp.h / 2;
    const translateX = vpCenterX - cardCenterX;
    const translateY = vpCenterY - cardCenterY;
    return `translate3d(${translateX}px, ${translateY}px, 0)`;
  };

  const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const refillBag = (current) => {
    const ids = base.map(b => b.id);
    const bag = shuffle(ids);
    if (bag[bag.length - 1] === current) bag.unshift(bag.pop());
    bagRef.current = bag;
  };

  const getNextRandomId = () => {
    if (!bagRef.current || bagRef.current.length === 0) refillBag(activeIdRef.current);
    return bagRef.current.pop();
  };

  const flyTo = (id, opts = {}) => {
    if (!baseById.get(id)) return;
    if (id === activeIdRef.current) return;
    if (opts.immediate) setTransitionEnabled(false);
    activeIdRef.current = id;
    setActiveId(id);
    setTransform(computeTransformFor(id, vpSizeRef.current));
    if (opts.immediate) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setTransitionEnabled(true));
      });
    }
  };

  const startAutoplay = () => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
    if (!autoplay || base.length <= 1) return;
    autoplayRef.current = setInterval(() => {
      const nextId = getNextRandomId();
      if (typeof nextId === "number") flyTo(nextId);
    }, Math.max(600, Number(intervalMs) || 2200));
  };

  useEffect(() => {
    if (!viewportRef.current) return;
    const el = viewportRef.current;
    const update = () => {
      const rect = el.getBoundingClientRect();
      const vp = { w: Math.max(0, rect.width), h: Math.max(0, rect.height) };
      vpSizeRef.current = vp;
      setViewport(vp);
    };
    update();

    let ro;
    try {
      ro = new ResizeObserver(() => update());
      ro.observe(el);
    } catch (_) {
      window.addEventListener("resize", update);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", update);
    };
  }, []);

  // Initialize transform once viewport is known
  useEffect(() => {
    if (!viewport.w || !viewport.h || base.length === 0) return;
    setTransitionEnabled(false);
    const startId = baseById.get(activeIdRef.current ?? defaultStart) ? (activeIdRef.current ?? defaultStart) : defaultStart;
    activeIdRef.current = startId;
    setActiveId(startId);
    setTransform(computeTransformFor(startId, vpSizeRef.current));
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setTransitionEnabled(true));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewport.w, viewport.h, base.length]);

  useEffect(() => {
    startAutoplay();
    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay, intervalMs, base.length]);

  if (base.length === 0) return null;

  const viewportHeight = Math.max(420, Number(height) || 800);

  const hasText = showText && (eyebrow || heading || description);
  const isRight = textPosition === "right";

  const textPanel = hasText && (
    <div className="md:flex-[0_0_280px] relative z-10 flex flex-col justify-center">
      {eyebrow && (
        <div className="text-[11px] font-semibold tracking-[.13em] uppercase text-[#6c47ff] mb-3">
          {eyebrow}
        </div>
      )}
      {heading && (
        <h2 className="text-3xl md:text-[2.2rem] font-bold leading-[1.18] text-[#111] mb-4 whitespace-pre-line">
          {heading}
        </h2>
      )}
      {description && (
        <p className="text-sm text-[#666] leading-[1.7]">
          {description}
        </p>
      )}
    </div>
  );

  // Distance-based card style: cards near active are slightly blurred,
  // farther ones are heavily blurred + faded, very far ones invisible.
  const activeBase = baseById.get(activeId);
  const getCardStyle = (b, isCenterCluster) => {
    if (!isCenterCluster) {
      // Ghost clusters on the edges — always heavily faded
      return { transform: "scale(0.82) translateZ(0)", opacity: 0.18, filter: "blur(8px)", zIndex: 1 };
    }
    if (b.id === activeId) {
      return { transform: "scale(1) translateZ(0)", opacity: 1, filter: "blur(0px)", zIndex: 10, boxShadow: "0 25px 60px rgba(0,0,0,0.18)", pointerEvents: "none" };
    }
    if (!activeBase) {
      return { transform: "scale(0.88) translateZ(0)", opacity: 0.5, filter: "blur(6px)", zIndex: 1 };
    }
    // Grid distance: how many columns/rows away
    const activCol = activeBase.id % COLS;
    const activRow = Math.floor(activeBase.id / COLS);
    const bCol = b.id % COLS;
    const bRow = Math.floor(b.id / COLS);
    const dist = Math.sqrt(Math.pow(bCol - activCol, 2) + Math.pow(bRow - activRow, 2));
    // dist=1 (adjacent): subtle blur | dist=2: more | dist>=2.5: heavy+fade
    if (dist <= 1.1) return { transform: "scale(0.92) translateZ(0)", opacity: 0.72, filter: "blur(4px)", zIndex: 3 };
    if (dist <= 1.8) return { transform: "scale(0.88) translateZ(0)", opacity: 0.45, filter: "blur(7px)", zIndex: 2 };
    return { transform: "scale(0.84) translateZ(0)", opacity: 0.22, filter: "blur(10px)", zIndex: 1 };
  };

  /* ── MOBILE: stacked text + fade carousel ── */
  const mobileCarousel = (
    <div className="md:hidden w-full">
      {hasText && (
        <div className="mb-6 px-1">
          {eyebrow && <div className="text-[11px] font-semibold tracking-[.13em] uppercase text-[#6c47ff] mb-2">{eyebrow}</div>}
          {heading && <h2 className="text-2xl font-bold leading-[1.2] text-[#111] mb-3 whitespace-pre-line">{heading}</h2>}
          {description && <p className="text-sm text-[#666] leading-[1.7]">{description}</p>}
        </div>
      )}
      {/* Single card + bleed peek of neighbours */}
      <div className="relative w-full overflow-hidden" style={{ height: "420px" }}>
        {base.map((b) => {
          const isActive = b.id === activeId;
          const prev = (activeId - 1 + base.length) % base.length;
          const next = (activeId + 1) % base.length;
          const isPrev = b.id === prev;
          const isNext = b.id === next;
          let mobileStyle = {};
          if (isActive) {
            mobileStyle = { transform: "translateX(0) scale(1)", opacity: 1, filter: "blur(0)", zIndex: 10, pointerEvents: "none" };
          } else if (isPrev) {
            mobileStyle = { transform: "translateX(-88%) scale(0.85)", opacity: 0.5, filter: "blur(3px)", zIndex: 5 };
          } else if (isNext) {
            mobileStyle = { transform: "translateX(88%) scale(0.85)", opacity: 0.5, filter: "blur(3px)", zIndex: 5 };
          } else {
            mobileStyle = { transform: "translateX(0) scale(0.8)", opacity: 0, filter: "blur(6px)", zIndex: 1, pointerEvents: "none" };
          }
          return (
            <button
              key={`mob-${b.id}`}
              type="button"
              className="stw-mob-card"
              style={mobileStyle}
              onClick={isActive ? undefined : () => { flyTo(b.id); startAutoplay(); }}
              aria-label={`Focus image ${b.id + 1}`}
            >
              <img src={b.src} alt={b.alt} style={{ objectFit: b.objectFit }} draggable={false} />
            </button>
          );
        })}
        {/* Dot indicators */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
          {base.map((b) => (
            <button
              key={`dot-${b.id}`}
              type="button"
              onClick={() => { flyTo(b.id); startAutoplay(); }}
              className="stw-dot"
              style={{ background: b.id === activeId ? "#111" : "rgba(0,0,0,0.25)", transform: b.id === activeId ? "scale(1.3)" : "scale(1)" }}
              aria-label={`Go to image ${b.id + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );

  /* ── DESKTOP: infinite panning wall ── */
  const desktopWall = (
    <div className={`hidden md:flex ${isRight ? "flex-row-reverse" : "flex-row"} items-center gap-12 lg:gap-16`}>
      {textPanel}
      {/* Viewport — the "camera window", never moves */}
      <div
        ref={viewportRef}
        className="flex-1 relative overflow-hidden rounded-[20px] bg-[#e9e9eb]"
        style={{ height: `${viewportHeight}px`, perspective: "1000px", boxShadow: "inset 0 0 40px rgba(0,0,0,0.03)" }}
      >
        {/* Track — the "world" that pans */}
        <div
          className={`stw-track absolute top-0 left-0 ${transitionEnabled ? "" : "stw-no-transition"}`}
          style={{ transform, transformStyle: "preserve-3d", width: 0, height: 0 }}
        >
          {[-1, 0, 1].flatMap(dy =>
            [-1, 0, 1].flatMap(dx =>
              base.map((b) => {
                const top = b.top + dy * clusterHeight;
                const left = b.left + dx * clusterWidth;
                const isCenterCluster = dx === 0 && dy === 0;
                const isActive = isCenterCluster && b.id === activeId;
                const cardStyle = getCardStyle(b, isCenterCluster);
                return (
                  <button
                    key={`${dx}:${dy}:${b.id}`}
                    type="button"
                    className="stw-card"
                    style={{ top, left, ...cardStyle }}
                    onClick={isActive ? undefined : () => { flyTo(b.id); startAutoplay(); }}
                    aria-label={`Focus image ${b.id + 1}`}
                  >
                    <img src={b.src} alt={b.alt} style={{ objectFit: b.objectFit }} draggable={false} />
                  </button>
                );
              })
            )
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {mobileCarousel}
      {desktopWall}

      <style>{`
        /* Desktop panning wall */
        .stw-track {
          will-change: transform;
          transition: transform 1200ms cubic-bezier(0.4, 0, 0.2, 1);
        }
        .stw-no-transition { transition: none !important; }
        .stw-card {
          position: absolute;
          width: ${CARD_W}px;
          height: ${CARD_H}px;
          background: #fff;
          border-radius: 14px;
          overflow: hidden;
          cursor: pointer;
          transition: transform 1200ms cubic-bezier(0.4, 0, 0.2, 1),
                      opacity 1200ms cubic-bezier(0.4, 0, 0.2, 1),
                      filter 1200ms cubic-bezier(0.4, 0, 0.2, 1),
                      box-shadow 1200ms cubic-bezier(0.4, 0, 0.2, 1);
          will-change: transform, opacity, filter;
          padding: 0; border: none;
        }
        .stw-card img { width: 100%; height: 100%; pointer-events: none; display: block; }

        /* Mobile carousel */
        .stw-mob-card {
          position: absolute;
          top: 0; left: 50%;
          width: 240px; height: 340px;
          margin-left: -120px;
          background: #fff;
          border-radius: 14px;
          overflow: hidden;
          cursor: pointer;
          padding: 0; border: none;
          box-shadow: 0 12px 40px rgba(0,0,0,0.12);
          transition: transform 700ms cubic-bezier(0.4, 0, 0.2, 1),
                      opacity 700ms cubic-bezier(0.4, 0, 0.2, 1),
                      filter 700ms cubic-bezier(0.4, 0, 0.2, 1);
          will-change: transform, opacity;
        }
        .stw-mob-card img { width: 100%; height: 100%; object-fit: cover; pointer-events: none; display: block; }
        .stw-dot {
          width: 6px; height: 6px; border-radius: 50%; border: none; cursor: pointer; padding: 0;
          transition: transform 300ms ease, background 300ms ease;
        }
      `}</style>
    </div>
  );
}

/* ========================= CATEGORY BROWSER ========================= */
function CategoryBrowser({ block = {} }) {
  const {
    title = "Browse by Category",
    onlyCategories = "",
  } = block;

  const [categories, setCategories] = useState([]);
  const [catsLoading, setCatsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const PER_PAGE = 4;

  const { ref: sectionRef, inView } = useInView({ rootMargin: "200px" });

  useEffect(() => {
    if (!inView) return;
    setCatsLoading(true);
    api.get("/books/categories")
      .then(res => {
        const raw = res.data;
        let list = [];
        if (Array.isArray(raw)) list = raw;
        else if (Array.isArray(raw?.categories)) list = raw.categories;
        else if (Array.isArray(raw?.items)) list = raw.items;

        if (onlyCategories) {
          const allowed = onlyCategories.split(",").map(s => s.trim().toLowerCase()).filter(Boolean);
          list = list.filter(c => {
            const name = (typeof c === "string" ? c : c?.name || "").toLowerCase();
            const slug = (typeof c === "string" ? c : c?.slug || "").toLowerCase();
            return allowed.includes(name) || allowed.includes(slug);
          });
        }
        setCategories(list);
      })
      .catch(() => setCategories([]))
      .finally(() => setCatsLoading(false));
  }, [inView, onlyCategories]);

  return (
    <section ref={sectionRef} className="px-4 sm:px-6 lg:px-8 py-10 md:py-14">

      {/* Header */}
      <div className="flex items-center justify-between mb-8 border-b border-[#D4AF37]/20 pb-4">
        <h2 className="text-3xl md:text-4xl font-['Cinzel'] font-bold text-[#3E2723] flex items-center gap-4">
          <span className="w-1.5 h-8 bg-[#D4AF37] rounded-full inline-block" />
          {title}
        </h2>
        <Link
          to="/categories"
          className="text-[#D4AF37] hover:text-[#3E2723] font-bold text-sm flex items-center gap-1 transition-colors uppercase tracking-widest font-['Cinzel']"
        >
          View All <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Category Cards — paginated carousel, 4 at a time */}
      {catsLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="rounded-2xl bg-[#3E2723]/10 animate-pulse aspect-[3/4]" />
          ))}
        </div>
      ) : categories.length === 0 ? (
        <div className="rounded-[2rem] border border-[#D4AF37]/20 bg-white/50 p-12 text-center text-[#8A7A5E] italic font-['Cinzel']">
          No categories found.
        </div>
      ) : (() => {
        const totalPages = Math.ceil(categories.length / PER_PAGE);
        const visible = categories.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);
        return (
          <div>
            {/* Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
              {visible.map((cat) => {
                const name = typeof cat === "string" ? cat : cat?.name || "";
                const slug = typeof cat === "string" ? cat : cat?.slug || cat?.name || "";
                const count = cat?.count ?? null;
                const image = cat?.image ? assetUrl(cat.image, "thumb") : null;
                const hoverImage = cat?.hoverImage ? assetUrl(cat.hoverImage, "thumb") : null;
                if (!name) return null;
                return (
                  <Link
                    key={slug}
                    to={`/category/${encodeURIComponent(slug)}`}
                    className="group flex flex-col rounded-2xl overflow-hidden bg-white/80 border border-[#D4AF37]/20 hover:border-[#D4AF37] shadow-sm hover:shadow-[0_8px_28px_rgba(212,175,55,0.25)] transition-all duration-300"
                  >
                    <div className="relative w-full aspect-[4/3] bg-[#F3E5AB]/50 overflow-hidden">
                      {image ? (
                        <>
                          <img
                            src={image}
                            alt={name}
                            loading="lazy"
                            className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${hoverImage ? "opacity-100 group-hover:opacity-0" : "group-hover:scale-105"}`}
                          />
                          {hoverImage && (
                            <img
                              src={hoverImage}
                              alt={name}
                              loading="lazy"
                              className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 scale-105 group-hover:scale-100 transition-all duration-500"
                            />
                          )}
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="w-14 h-14 rounded-full bg-[#F3E5AB] flex items-center justify-center group-hover:bg-[#D4AF37] transition-colors duration-300">
                            <svg className="w-7 h-7 text-[#3E2723]" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                            </svg>
                          </div>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                    <div className="px-3 py-3 flex flex-col gap-0.5">
                      <span className="font-['Cinzel'] font-bold text-[#3E2723] text-xs md:text-sm leading-tight line-clamp-2 uppercase tracking-wide">
                        {name}
                      </span>
                      {count !== null && (
                        <span className="text-[10px] md:text-xs text-[#8A7A5E] font-['Cinzel'] uppercase tracking-wider">
                          {count} {count === 1 ? "book" : "books"}
                        </span>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Navigation */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8">
                <button
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 0}
                  className="w-10 h-10 rounded-full border-2 border-[#D4AF37]/40 flex items-center justify-center text-[#3E2723] hover:border-[#D4AF37] hover:bg-[#FFF9E6] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-2">
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i)}
                      className={`transition-all duration-300 rounded-full ${i === page
                          ? "w-6 h-2.5 bg-[#D4AF37]"
                          : "w-2.5 h-2.5 bg-[#D4AF37]/30 hover:bg-[#D4AF37]/60"
                        }`}
                    />
                  ))}
                </div>

                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page === totalPages - 1}
                  className="w-10 h-10 rounded-full border-2 border-[#D4AF37]/40 flex items-center justify-center text-[#3E2723] hover:border-[#D4AF37] hover:bg-[#FFF9E6] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/* Browse all categories button */}
      <div className="flex justify-center mt-10">
        <Link
          to="/categories"
          className="inline-flex items-center gap-3 px-8 py-3.5 rounded-full border-2 border-[#D4AF37] text-[#3E2723] font-['Cinzel'] font-bold text-sm uppercase tracking-widest hover:bg-[#D4AF37] hover:text-[#3E2723] transition-all duration-300 shadow-[0_4px_14px_rgba(212,175,55,0.2)] hover:shadow-[0_4px_20px_rgba(212,175,55,0.4)]"
        >
          Browse all categories <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

/* ------------------------- Grid Section & Cards ------------------------- */
// âœ… FIXED: Converted from Multi-Row Grid to Single Line Slider
function GridSection({ title, query }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const layout = query?.layout || "classic";
  const limit = Number(query?.limit || 12);
  const q = query?.q || "";
  const sort = query?.sort || "new";

  const scrollRef = useRef(null);
  const scrollInterval = useRef(null); // Ref for the auto-scroll timer

  // Only fetch when this section scrolls near the viewport
  const { ref: sectionRef, inView } = useInView({ rootMargin: "200px" });

  const categories = (query?.category || "")
    .split(",")
    .map(s => s.trim())
    .filter(Boolean);

  useEffect(() => {
    if (!inView) return; // wait until section is near viewport

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const params = { q, sort, limit };
        if (categories.length) {
          params.category = categories.join(",");
        }

        const { data } = await api.get("/books", { params });
        if (!cancelled) {
          let list = Array.isArray(data.items) ? data.items : [];
          setItems(list.slice(0, limit));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [inView, q, sort, limit, JSON.stringify(categories)]);

  // --- AUTO SCROLL LOGIC ---
  const startAutoScroll = () => {
    stopAutoScroll(); // clear any existing
    scrollInterval.current = setInterval(() => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        // If we reached the end, reset to start, otherwise scroll right
        if (scrollLeft + clientWidth >= scrollWidth - 10) {
          scrollRef.current.scrollTo({ left: 0, behavior: "smooth" });
        } else {
          scrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
        }
      }
    }, 3000); // Scrolls every 3 seconds
  };

  const stopAutoScroll = () => {
    if (scrollInterval.current) clearInterval(scrollInterval.current);
  };

  // Start auto-scroll when items are loaded
  useEffect(() => {
    if (items.length > 0) {
      startAutoScroll();
    }
    return () => stopAutoScroll(); // Cleanup on unmount
  }, [items]);

  // Manual Scroll Handler
  const scroll = (direction) => {
    stopAutoScroll(); // Pause auto-scroll when user interacts
    if (scrollRef.current) {
      const { current } = scrollRef;
      const scrollAmount = direction === "left" ? -300 : 300;
      current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
    // Restart auto-scroll after interaction (optional, or leave it off)
    setTimeout(startAutoScroll, 5000);
  };

  return (
    <section
      ref={sectionRef}
      className="relative group/slider overflow-x-clip"
      onMouseEnter={stopAutoScroll}
      onMouseLeave={startAutoScroll}
    >
      <div className="max-w-screen-2xl mx-auto px-4">
        {/* Show skeleton until data is fetched */}
        {(loading || (!inView && items.length === 0)) && <GridSectionSkeleton />}

        {/* Render actual content once loaded */}
        {!loading && inView && (
          <>
            {title && (
              <div className="flex items-center justify-between mb-6 md:mb-8 border-b border-[#D4AF37]/20 pb-4">
                <h3 className="text-3xl md:text-4xl font-['Cinzel'] font-bold text-[#3E2723] flex items-center gap-4">
                  <span className="w-1.5 h-8 bg-[#D4AF37] rounded-full"></span>
                  {title}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => scroll("left")}
                    className="w-8 h-8 rounded-full bg-white border border-[#D4AF37]/40 flex items-center justify-center text-[#3E2723] shadow-sm hover:bg-[#D4AF37] hover:text-white transition-all active:scale-95"
                    aria-label="Scroll Left"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => scroll("right")}
                    className="w-8 h-8 rounded-full bg-white border border-[#D4AF37]/40 flex items-center justify-center text-[#3E2723] shadow-sm hover:bg-[#D4AF37] hover:text-white transition-all active:scale-95"
                    aria-label="Scroll Right"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <Link to="/catalog" className="text-[#D4AF37] hover:text-[#3E2723] font-bold text-sm flex items-center gap-1 transition-colors uppercase tracking-widest font-['Cinzel'] ml-1">
                    View All <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            )}

            {!items.length ? (
              <div className="rounded-[2rem] border border-[#D4AF37]/20 bg-white/50 p-12 text-center text-[#8A7A5E] italic font-['Cinzel']">No sacred treasures found.</div>
            ) : (
              <>

                {/* Slider Container */}
                <div
                  ref={scrollRef}
                  className="flex gap-4 sm:gap-6 overflow-x-auto snap-x snap-mandatory pb-8 scroll-smooth no-scrollbar"
                >
                  {items.map((b) => (
                    <div
                      key={b._id || b.id}
                      className="w-[calc(100%-1rem)] sm:w-[calc(50%-12px)] md:w-[calc(33.333%-16px)] lg:w-[calc(25%-18px)] xl:w-[calc(20%-20px)] 2xl:w-[calc(16.666%-20px)] flex-shrink-0 snap-start h-full"
                    >
                      <div className="h-full">
                        {layout === "classic" ? <ProductCard book={b} /> : <SimpleShowcaseCard book={b} />}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function SimpleShowcaseCard({ book }) {
  const mrp = Number(book.mrp) || 0;
  const price = Number(book.price) || 0;
  const off = mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;

  const coverArr = Array.isArray(book.assets?.coverUrl) ? book.assets.coverUrl : [book.assets?.coverUrl];
  const img1 = assetUrl(coverArr[0], "thumb");
  const img2 = coverArr[1] ? assetUrl(coverArr[1], "thumb") : null;

  return (
    <article className="group flex flex-col h-full bg-white rounded-[1.5rem] border border-[#D4AF37]/20 overflow-hidden hover:border-[#D4AF37] hover:shadow-[0_10px_30px_rgba(212,175,55,0.2)] transition-all duration-500 relative">
      <Link to={`/book/${book.slug}`} className="relative block w-full aspect-[2/3] bg-[#FAF7F2] overflow-hidden">
        {/* Subtle Mandala Texture */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: mandalaBg }}></div>
        <div className="w-full h-full relative z-10">
          {/* Primary image */}
          <img
            src={img1}
            alt={book.title}
            className={`absolute inset-0 w-full h-full object-contain object-top pt-0 px-2 sm:px-3 drop-shadow-md transition-all duration-500 ease-out ${img2 ? "opacity-100 group-hover:opacity-0" : "group-hover:scale-110 group-hover:rotate-1"}`}
            loading="lazy"
            decoding="async"
          />
          {/* Secondary image (hover) */}
          {img2 && (
            <img
              src={img2}
              alt={book.title}
              className="absolute inset-0 w-full h-full object-contain object-top pt-0 px-2 sm:px-3 drop-shadow-md transition-all duration-500 ease-out opacity-0 group-hover:opacity-100 group-hover:scale-105"
              loading="lazy"
              decoding="async"
            />
          )}
        </div>
        {off > 0 && <span className="absolute top-4 left-4 bg-[#3E2723] text-[#F3E5AB] text-[10px] font-bold px-2 py-1 rounded border border-[#D4AF37] shadow-sm z-20">-{off}%</span>}
      </Link>
      <div className="p-5 flex flex-col flex-grow text-center relative z-10">
        <Link to={`/book/${book.slug}`} className="font-['Cinzel'] font-bold text-[#3E2723] text-lg leading-tight line-clamp-2 mb-2 group-hover:text-[#D4AF37] transition-colors">{book.title}</Link>
        <div className="mt-auto pt-3 border-t border-[#D4AF37]/10 flex flex-col items-center gap-1">
          <span className="font-bold text-[#3E2723] text-xl font-['Playfair_Display']">â‚¹{price}</span>
          {off > 0 && <span className="text-xs line-through text-[#8A7A5E] font-['Inter'] decoration-[#D4AF37]/50">â‚¹{mrp}</span>}
        </div>
      </div>
    </article>
  );
}

/* ─────────────────────────── VIDEO SHOWCASE ─────────────────────────────── */
function VideoCard({ vid }) {
  const videoRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = true;
    el.loop = true;
    const tryPlay = () => el.play().catch(() => { });
    el.addEventListener("canplay", tryPlay, { once: true });
    tryPlay();
    return () => el.removeEventListener("canplay", tryPlay);
  }, []);

  const toggleMute = (e) => {
    e.stopPropagation();
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  const thumbSrc = vid.thumbnail ? assetUrl(vid.thumbnail) : "";
  const bookCoverSrc = vid.bookCover ? assetUrl(vid.bookCover) : thumbSrc;
  const videoSrc = assetUrl(vid.videoUrl);
  const hasBook = !!vid.bookSlug;

  return (
    <div
      className="relative flex-shrink-0 snap-start w-[72vw] sm:w-[44vw] md:w-[28vw] lg:w-[22vw] rounded-2xl overflow-hidden shadow-md cursor-pointer bg-gray-100"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Video */}
      <div className="relative w-full aspect-[9/16]">
        <video
          ref={videoRef}
          src={videoSrc}
          poster={thumbSrc || undefined}
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />

        {/* Mute / Unmute button — always visible, above everything including the hover overlay */}
        <button
          onClick={toggleMute}
          aria-label={muted ? "Unmute" : "Mute"}
          className="absolute bottom-3 left-3 z-30 w-8 h-8 rounded-full bg-black/50 border border-white/20 flex items-center justify-center text-white backdrop-blur-sm hover:bg-black/70 transition-all"
        >
          {muted ? (
            /* muted icon */
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707A1 1 0 0112 5v14a1 1 0 01-1.707.707L5.586 15z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
            </svg>
          ) : (
            /* unmuted icon */
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072M12 6v12m-6.414-3H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707A1 1 0 0112 5v14a1 1 0 01-1.707.707L5.586 15z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.07 4.93a10 10 0 010 14.14" />
            </svg>
          )}
        </button>

        {/* Hover overlay — full card takeover */}
        {hasBook && (
          <div
            className="absolute inset-0 z-10 transition-opacity duration-300"
            style={{
              opacity: hovered ? 1 : 0,
              pointerEvents: hovered ? "auto" : "none",
            }}
          >
            {/* Full book cover fills the entire card */}
            {bookCoverSrc && (
              <img
                src={bookCoverSrc}
                alt={vid.bookTitle || vid.title || "Book"}
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}

            {/* Dark gradient at bottom for buttons */}
            <div
              className="absolute inset-0"
              style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 45%, transparent 100%)" }}
            />

            {/* Buttons pinned to bottom */}
            <div className="absolute bottom-0 left-0 right-0 z-20 flex flex-col gap-2 px-4 pb-5">
              {(vid.bookTitle || vid.title) && (
                <p className="text-white font-bold text-sm text-center leading-snug drop-shadow-lg mb-1">
                  {vid.bookTitle || vid.title}
                </p>
              )}
              <Link
                to={`/book/${vid.bookSlug}`}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl
                           bg-gradient-to-r from-[#C59D5F] to-[#B0894C] text-white text-xs font-bold
                           uppercase tracking-wide hover:from-[#D4AF37] hover:to-[#C59D5F] transition-all shadow-lg"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13l-1.5 6h13M7 13L5.4 5M10 21a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
                </svg>
                Buy Now
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Title below video */}
      {vid.title && (
        <div className="bg-white px-3 py-2 text-center">
          <p className="text-[#3E2723] text-xs font-semibold truncate">{vid.title}</p>
        </div>
      )}
    </div>
  );
}

function VideoShowcase({ block, videos }) {
  const trackRef = useRef(null);
  const total = videos.length;

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === "left" ? -320 : 320, behavior: "smooth" });
  };

  return (
    <section className="py-10 md:py-16 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        {(block.title || block.subtitle) && (
          <div className="text-center mb-8 md:mb-12">
            {block.title && (
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#3E2723] mb-2 tracking-tight">
                {block.title}
              </h2>
            )}
            {block.subtitle && (
              <p className="text-[#8D7F71] text-sm md:text-base max-w-xl mx-auto">{block.subtitle}</p>
            )}
          </div>
        )}

        {/* Slider wrapper */}
        <div className="relative group/slider">
          {/* Left arrow */}
          <button
            onClick={() => scroll("left")}
            aria-label="Scroll left"
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-20 w-10 h-10 rounded-full bg-white border border-[#EBE5D9] shadow-md text-[#3E2723] flex items-center justify-center opacity-0 group-hover/slider:opacity-100 hover:bg-[#D4AF37] hover:text-white hover:border-[#D4AF37] transition-all duration-200"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          </button>

          {/* Right arrow */}
          <button
            onClick={() => scroll("right")}
            aria-label="Scroll right"
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-20 w-10 h-10 rounded-full bg-white border border-[#EBE5D9] shadow-md text-[#3E2723] flex items-center justify-center opacity-0 group-hover/slider:opacity-100 hover:bg-[#D4AF37] hover:text-white hover:border-[#D4AF37] transition-all duration-200"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
          </button>

          {/* Cards track */}
          <div
            ref={trackRef}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar pb-2"
            style={{ scrollbarWidth: "none" }}
          >
            {videos.map((vid, idx) => (
              <VideoCard key={idx} vid={vid} />
            ))}
          </div>
        </div>

        {/* Scroll indicator bar */}
        {total > 3 && (
          <div className="mt-6 mx-auto w-24 h-1 rounded-full bg-[#EBE5D9] overflow-hidden">
            <div className="h-full bg-[#D4AF37] rounded-full w-1/3" />
          </div>
        )}
      </div>
    </section>
  );
}

function GridSectionSkeleton() {
  return (
    <section className="relative">
      <div className="flex items-center justify-between mb-6 md:mb-8 border-b border-[#D4AF37]/20 pb-4">
        <div className="h-8 w-56 bg-[#3E2723]/10 rounded animate-pulse" />
        <div className="h-4 w-20 bg-[#3E2723]/10 rounded animate-pulse" />
      </div>
      <div className="flex gap-6 overflow-hidden pb-8">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="w-[calc(100vw-3.5rem)] sm:w-[calc(25%-18px)] flex-shrink-0 h-[340px] rounded-[1.5rem] bg-[#3E2723]/10 animate-pulse" />
        ))}
      </div>
    </section>
  );
}


